import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import type { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import { useDispatch } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import logSignUpScreenDismissed from '@alltrails/amplitude/events/logSignUpScreenDismissed';
import logWebSignUpModalLogInTapped from '@alltrails/amplitude/events/logWebSignUpModalLogInTapped';
import logWebSignUpModalShown from '@alltrails/amplitude/events/logWebSignUpModalShown';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import PurchaseTier from '@alltrails/core/types/PurchaseTier';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { ModalStep, MultiStepModal, useMultiStepModal } from '@alltrails/modal';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type { User } from '@alltrails/types/User';
import CheckoutModalStep from '@alltrails/upsell/components/CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '@alltrails/upsell/components/CheckoutWelcome/CheckoutWelcome';
import GenericPostAuthUpsellStep from '@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellStep';
import getDefaultTierSelected from '@alltrails/upsell/components/UpsellSystem/GenericUpsell/getDefaultTierSelected';
import useApplePopupHandler from '../../hooks/useApplePopupHandler';
import useFacebookHandler from '../../hooks/useFacebookHandler';
import useGoogleHandler from '../../hooks/useGoogleHandler';
import LoginResponse from '../../types/LoginResponse';
import EmailCheck from './EmailCheck';
import Initial from './Initial';
import InitialMobile from './InitialMobile';
import InitialMobileSimplified from './InitialMobileSimplified';
import LogIn from './LogIn';
import OAuthStep from './OAuthStep';
import PlusFeatureTriggers from './PlusFeatureTriggers';
import Register from './Register';
import styles from './SignUpModal.module.scss';

type Props = {
  autoClose?: boolean;
  closeModal: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => void;
  deeplinkData?: DeeplinkData;
  heading?: ReactNode;
  isOpen: boolean;
  onSuccess: (user: User) => void;
  redirectUrl?: string;
  hideUpsell?: boolean;
  hideAppPromotion?: boolean;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

type SignupModalSteps = 'checkout' | 'email-check' | 'initial' | 'initial-simplified' | 'login' | 'oauth-signin' | 'register' | 'upsell' | 'welcome';

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

export default function SignUpModal({
  autoClose,
  closeModal,
  deeplinkData,
  heading,
  isOpen,
  onSuccess,
  redirectUrl,
  hideUpsell = false,
  hideAppPromotion = false,
  trigger,
  upsellTrigger
}: Props) {
  const isMobile = useIsMobileSizedScreen();
  const modalRef = useRef<HTMLDivElement>(null);
  const applePopupHandler = useApplePopupHandler(isOpen);
  const facebookHandler = useFacebookHandler();
  const googleHandler = useGoogleHandler();
  const dispatch = useDispatch();
  const experiments = useExperiments();
  const sendConsolidatedAuthExposure = useExposureEvent('web-growth-rm-consolidate-auth');
  const sendSimplifiedAuthExposure = useExposureEvent('web-growth-rm-simplified-auth-v2');
  const defaultTierSelected = getDefaultTierSelected();

  const simplifiedAuthVariant = experiments['web-growth-rm-simplified-auth-v2']?.value;
  const isSimplifiedAuth = isMobile && (simplifiedAuthVariant === 'simplified-with-app-info' || simplifiedAuthVariant === 'simplified-no-app-info');
  const hideAppDetails = simplifiedAuthVariant === 'simplified-no-app-info';
  const initialStep: SignupModalSteps = isSimplifiedAuth ? 'initial-simplified' : 'initial';

  const { activeModal, navigateTo, goBack } = useMultiStepModal<SignupModalSteps>(initialStep);
  const setUser = useSetUser();

  const [tierSelected, setTierSelected] = useState<PurchaseTier>(defaultTierSelected);
  const [prefillEmail, setPrefillEmail] = useState<string>();
  const [oauthProvider, setOauthProvider] = useState<'Apple' | 'Google' | 'Facebook'>();
  const isConsolidatedAuth = experiments['web-growth-rm-consolidate-auth']?.value === 'treatment';

  const successCallback = useCallback(
    async ({ user, plans, currencyCode }: LoginResponse) => {
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        setUser(user);
        dispatch(setPlans(plans));
        dispatch(setCurrencyCode(currencyCode));

        global.google?.accounts.id.cancel(); // google can be undefined on mobile web

        const isInUpsellTier = user?.subscriptionTier === 'base';
        const shouldShowUpsell = !hideUpsell && isInUpsellTier && (!trigger || !PlusFeatureTriggers.includes(trigger));

        if (shouldShowUpsell) {
          navigateTo('upsell');
        } else {
          onSuccess(user);
          autoClose && closeModal();
        }
      }
    },
    [redirectUrl, setUser, hideUpsell, trigger, dispatch, navigateTo, onSuccess, autoClose, closeModal]
  );

  const handleCloseRequest = () => {
    logSignUpScreenDismissed({ signup_screen_dismissed_type: 'x' });
    closeModal();
  };

  const getLoginHandlers = useCallback(
    (isLogIn: boolean) => {
      const appleLoginHandler = async () => {
        if (isLogIn) {
          logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'apple' });
        } else {
          logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'apple' });
        }

        try {
          const loginResponse = await applePopupHandler({ handlerType: isLogIn ? 'login' : 'signup' });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      const googleLoginHandler = async (response: GoogleLoginResponse | GoogleLoginResponseOffline | string) => {
        const loginResponse = await googleHandler({ googleUserOrToken: response, handlerType: isLogIn ? 'login' : 'signup' });
        successCallback(loginResponse);
      };

      const facebookLoginHandler = async (response: ReactFacebookLoginInfo) => {
        try {
          const loginResponse = await facebookHandler({
            loginInfo: response,
            handlerType: isLogIn ? 'login' : 'signup'
          });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      return { appleLoginHandler, googleLoginHandler, facebookLoginHandler };
    },
    [applePopupHandler, facebookHandler, googleHandler, successCallback]
  );

  function getActiveModal() {
    switch (activeModal) {
      case 'login':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <LogIn
              handleLoginSuccess={successCallback}
              prefillEmail={prefillEmail}
              isConsolidatedAuth={isConsolidatedAuth}
              {...getLoginHandlers(true)}
            />
          </ModalStep>
        );

      case 'register':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <Register
              onLogInClick={() => {
                logWebSignUpModalLogInTapped();
                navigateTo('login');
              }}
              onSuccess={successCallback}
              prefillEmail={prefillEmail}
              isConsolidatedAuth={isConsolidatedAuth}
              onEmailAlreadyExists={email => {
                setPrefillEmail(email);
                navigateTo('login');
              }}
            />
          </ModalStep>
        );

      case 'email-check':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <EmailCheck
              onEmailAvailable={email => {
                setPrefillEmail(email);
                navigateTo('register');
              }}
              onEmailExists={email => {
                setPrefillEmail(email);
                navigateTo('login');
              }}
              onOAuthRequired={(email, provider) => {
                setPrefillEmail(email);
                setOauthProvider(provider);
                navigateTo('oauth-signin');
              }}
            />
          </ModalStep>
        );

      case 'oauth-signin':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <OAuthStep provider={oauthProvider!} {...getLoginHandlers(true)} />
          </ModalStep>
        );

      case 'initial':
        return (
          <ModalStep onCloseRequest={handleCloseRequest} hideBackButton={isMobile}>
            {isMobile ? (
              <InitialMobile
                heading={heading}
                displayTrigger={trigger}
                deeplinkData={deeplinkData}
                hideAppPromotion={hideAppPromotion}
                onContinueWithEmailClick={() => {
                  logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                  navigateTo(isConsolidatedAuth ? 'email-check' : 'register');
                }}
                onLogInClick={() => {
                  logWebSignUpModalLogInTapped();
                  navigateTo('login');
                }}
                {...getLoginHandlers(false)}
              />
            ) : (
              <Initial
                heading={heading}
                onCreateAccountClick={() => {
                  logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                  navigateTo(isConsolidatedAuth ? 'email-check' : 'register');
                }}
                onLogInClick={() => {
                  logWebSignUpModalLogInTapped();
                  navigateTo('login');
                }}
                {...getLoginHandlers(false)}
              />
            )}
          </ModalStep>
        );

      case 'initial-simplified':
        return (
          <ModalStep onCloseRequest={handleCloseRequest} hideBackButton>
            <InitialMobileSimplified
              heading={heading}
              displayTrigger={trigger}
              deeplinkData={deeplinkData}
              onMoreOptionsClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'more options' });
                navigateTo('initial');
              }}
              googleLoginHandler={getLoginHandlers(false).googleLoginHandler}
              hideAppDetails={hideAppDetails}
              hideAppPromotion={hideAppPromotion}
            />
          </ModalStep>
        );

      case 'upsell':
        return (
          <GenericPostAuthUpsellStep
            navigateTo={navigateTo}
            onCloseRequest={closeModal}
            onSuccess={onSuccess}
            tierSelected={tierSelected}
            setTierSelected={setTierSelected}
            upsellTrigger={upsellTrigger}
          />
        );

      case 'checkout':
        return (
          <CheckoutModalStep
            closeModal={closeModal}
            navigateToWelcome={() => navigateTo('welcome')}
            purchaseTier={tierSelected}
            trigger={upsellTrigger}
          />
        );

      case 'welcome':
        return (
          <CheckoutWelcome
            closeModal={user => {
              onSuccess(user);
              closeModal();
            }}
            purchaseTier={tierSelected}
          />
        );
    }
  }

  useEffect(() => {
    if (isOpen) {
      logWebSignUpModalShown({ trigger });
      sendConsolidatedAuthExposure();
    }
  }, [isOpen, sendConsolidatedAuthExposure, sendSimplifiedAuthExposure, trigger]);

  useEffect(() => {
    if (isOpen && isMobile) {
      sendSimplifiedAuthExposure();
    }
  }, [isOpen, isMobile, sendSimplifiedAuthExposure]);

  // Determine which steps should have the taller modal class
  const tallerSteps: SignupModalSteps[] = isMobile ? ['initial', 'checkout', 'welcome'] : ['upsell', 'checkout', 'welcome'];

  return (
    <MultiStepModal
      backgroundClassName={styles.background}
      ref={modalRef}
      isFullHeight={activeModal === 'checkout'}
      isOpen={isOpen}
      onBackRequest={activeModal === 'upsell' || activeModal === 'welcome' ? undefined : goBack}
      isPaused={activeModal === 'checkout'}
      onCloseRequest={closeModal}
      onCloseComplete={isMobile ? () => navigateTo(initialStep) : undefined}
      focusTrapOptions={{ allowOutsideClick: e => e.target !== modalRef.current, clickOutsideDeactivates: false }} // DISCO-10566 don't close the signup modal when install banner and signup modal both appear and user clicks outside of banner
      {...(tallerSteps.includes(activeModal) && !hideAppPromotion ? { modalClassName: styles.taller } : {})}
    >
      {getActiveModal()}
    </MultiStepModal>
  );
}
